import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Share, X, Smartphone } from "lucide-react";

// PWA install prompt. Modern Chromium browsers fire `beforeinstallprompt` when
// the site is installable; we capture the event and expose a manual install
// trigger. iOS Safari does not support the API - we show a text hint pointing
// users to Share → Add to Home Screen instead.
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem("aarya_install_dismissed") === "1") setDismissed(true);
    } catch {}
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (isStandalone) { setInstalled(true); return; }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => setInstalled(true));

    // Detect iOS Safari (no beforeinstallprompt support)
    const ua = window.navigator.userAgent || "";
    const isIos = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
    if (isIos) setShowIos(true);

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferred(null);
  };

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("aarya_install_dismissed", "1"); } catch {}
  };

  if (installed || dismissed) return null;
  const canPrompt = Boolean(deferred);
  const canIos = showIos && !canPrompt;
  if (!canPrompt && !canIos) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12 }}
        className="mt-4 rounded-3xl bg-gradient-to-br from-terracotta-50 to-sand-100 border border-terracotta-400/30 p-4 relative"
        data-testid="install-prompt-card"
      >
        <button
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          data-testid="install-prompt-dismiss"
          className="absolute top-3 right-3 h-7 w-7 rounded-full bg-white/60 flex items-center justify-center text-ink-500"
        >
          <X size={14}/>
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="h-10 w-10 rounded-2xl bg-terracotta-500 text-white flex items-center justify-center flex-shrink-0">
            <Smartphone size={18}/>
          </div>
          <div className="flex-1">
            <p className="text-[11px] tracking-[0.16em] uppercase text-terracotta-600 font-semibold">Get the full app</p>
            <p className="text-[15px] font-semibold text-ink-900 mt-0.5 leading-snug">
              Install Aarya on your phone
            </p>
            <p className="text-[12px] text-ink-700 leading-snug mt-1">
              Faster launch, always-on protection, home-screen icon. Free, no store account needed.
            </p>
          </div>
        </div>
        {canPrompt && (
          <button
            onClick={install}
            data-testid="install-prompt-install-btn"
            className="mt-3 w-full h-11 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white text-[14px] font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition"
          >
            <Download size={16}/>
            Install Aarya
          </button>
        )}
        {canIos && (
          <div className="mt-3 rounded-2xl bg-white border border-sand-200 p-3 text-[12px] text-ink-700 leading-snug flex items-start gap-2">
            <Share size={16} className="text-ink-500 mt-0.5 flex-shrink-0"/>
            <span>
              On iPhone: tap <strong>Share</strong> at the bottom of Safari, then <strong>Add to Home Screen</strong>.
              Aarya opens like a native app - faster and always ready.
            </span>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
